import { getPreferredLevel } from './levelPreference';

const STORAGE_KEY = 'cfa_acq_v1';
const EMAIL_ATTR_KEY = 'cfa_email_attr_v1';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const OPEN_ID_RE = /^[a-f0-9]{32}$/i;

function coarseDevice(ua = '') {
  const value = String(ua);
  if (/iPhone|iPad|iPod/i.test(value)) return 'iPhone';
  if (/Android/i.test(value)) return 'Android';
  if (/Mac OS X|Macintosh/i.test(value)) return 'Mac';
  if (/Windows/i.test(value)) return 'Windows';
  if (/Linux/i.test(value)) return 'Linux';
  return 'Other';
}

function readStored(key = STORAGE_KEY) {
  try {
    const raw = window.sessionStorage.getItem(key) || window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function writeStored(payload, key = STORAGE_KEY) {
  try {
    const raw = JSON.stringify(payload);
    window.sessionStorage.setItem(key, raw);
    window.localStorage.setItem(key, raw);
  } catch {
    /* private mode */
  }
}

function captureEmailLastTouch(params) {
  const em = String(params.get('em') || '')
    .trim()
    .toLowerCase();
  if (!OPEN_ID_RE.test(em)) return readStored(EMAIL_ATTR_KEY);

  const payload = {
    emailOpenId: em,
    emailCampaignId: String(params.get('ec') || '')
      .trim()
      .slice(0, 80),
    capturedAt: new Date().toISOString(),
    landingPath: String(window.location.pathname || '/').slice(0, 160),
  };
  writeStored(payload, EMAIL_ATTR_KEY);
  return payload;
}

/** Capture first-touch acquisition once per browser. Call early on app load.
 *  Email openId (`em`) is last-touch and updates whenever present. */
export function captureAcquisition() {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search || '');
  captureEmailLastTouch(params);

  const existing = readStored();
  if (existing?.capturedAt) return existing;

  const utm = {};
  UTM_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) utm[key] = String(value).slice(0, 120);
  });

  let referrer = '';
  try {
    referrer = String(document.referrer || '').slice(0, 300);
  } catch {
    referrer = '';
  }
  let referrerHost = '';
  try {
    if (referrer) referrerHost = new URL(referrer).hostname.slice(0, 120);
  } catch {
    referrerHost = '';
  }

  const payload = {
    capturedAt: new Date().toISOString(),
    landingPath: String(window.location.pathname || '/').slice(0, 160),
    referrer,
    referrerHost,
    device: coarseDevice(navigator.userAgent || ''),
    locale: String(navigator.language || '').slice(0, 32),
    ...utm,
  };
  writeStored(payload);
  return payload;
}

export function getAcquisition() {
  if (typeof window === 'undefined') return null;
  return readStored() || captureAcquisition();
}

export function getEmailAttribution() {
  if (typeof window === 'undefined') return null;
  return readStored(EMAIL_ATTR_KEY);
}

/** Fields for Stripe PaymentIntent / Checkout Session metadata + funnel props. */
export function getCheckoutAttribution() {
  if (typeof window === 'undefined') return {};
  const email = getEmailAttribution() || {};
  const acq = getAcquisition() || {};
  const out = {};
  if (email.emailOpenId) out.emailOpenId = email.emailOpenId;
  if (email.emailCampaignId) out.emailCampaignId = email.emailCampaignId;
  else if (acq.utm_campaign) out.emailCampaignId = String(acq.utm_campaign).slice(0, 80);
  if (acq.utm_source) out.utmSource = String(acq.utm_source).slice(0, 80);
  if (acq.utm_medium) out.utmMedium = String(acq.utm_medium).slice(0, 80);
  if (acq.utm_campaign) out.utmCampaign = String(acq.utm_campaign).slice(0, 80);
  const preferredLevel = getPreferredLevel('l1');
  if (preferredLevel) out.preferredLevel = preferredLevel;
  return out;
}

/** Fields to merge into userProfiles (first-touch only). */
export function acquisitionProfileFields(existing = {}) {
  if (existing.acqCapturedAt) return {};
  const acq = getAcquisition();
  if (!acq) return {};
  const fields = {
    acqCapturedAt: acq.capturedAt || new Date().toISOString(),
    acqLandingPath: acq.landingPath || '',
    acqReferrer: acq.referrer || '',
    acqReferrerHost: acq.referrerHost || '',
    acqDevice: acq.device || '',
    acqLocale: acq.locale || '',
  };
  UTM_KEYS.forEach((key) => {
    if (acq[key]) fields[`acq_${key}`] = acq[key];
  });
  const email = getEmailAttribution();
  if (email?.emailOpenId && !existing.acqEmailOpenId) {
    fields.acqEmailOpenId = email.emailOpenId;
    if (email.emailCampaignId) fields.acqEmailCampaignId = email.emailCampaignId;
  }
  return fields;
}
